import React, { useState, useEffect } from 'react';
import { ArcherElement } from 'react-archer';
import RuleEngineBackend from '../../services/RuleEngineBackend';
import PPUtils from '../../../utils/PPUtils';
import { hasConfiguredMargeOperand } from '../utils/margeFormula';

const displayValue = (value, fallback = "") => {
     if (value === null || value === undefined) return fallback;
     if (typeof value === "object") return value.label ?? value.value ?? fallback;
     return String(value);
};

const finiteNumber = (value) => {
     const parsed = Number(value);
     return Number.isFinite(parsed) ? parsed : null;
};

export default function ActionRuleCard({ obj, index, onEditRule, onDeleteRule }) {
     switch (obj.keyString) {
          case 'priceChangeMargin': return <PriceChangeMarginTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'calculationChannelAndRulePrice': return <CalculationChannelAndRulePriceTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'roundPrice': return <RoundPriceTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'changeInterval': return <ChangeIntervalTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'priceChange': return <PriceChangeTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'maxPriceAdjustment': return <MaxPriceChangeTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          case 'amazonBuyBox': return <AmazonBuyBoxTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule} />
          default: return <h1>Error</h1>
     }
}

function PriceChangeMarginTile({ ruledata, index, onEditRule, onDeleteRule }) {
     const formatFormula = (formula) => {
          if (!formula || !hasConfiguredMargeOperand(formula)) return "";

          const formatOperator = (op) => {
               switch (op) {
                    case "PLUS": return "+";
                    case "MINUS": return "-";
                    case "MULTIPLICATION": return "*";
                    case "DIVISION": return "/";
                    default: return op;
               }
          };

          const formatValue = (value) => {
               if (value === "NaN" || isNaN(value) || value === null || value === undefined) return "0";
               return value;
          };

          let formulaText = displayValue(ruledata.minimumPriceFormulaBase, "Einkaufspreis");

          if (formula.OP_1 && formula.X !== undefined) {
               formulaText += ` ${formatOperator(formula.OP_1)} ${formatValue(formula.X)}`;
          }
          if (formula.OP_2 && formula.Y !== undefined) {
               formulaText += ` ${formatOperator(formula.OP_2)} ${formatValue(formula.Y)}%`;
          }
          if (formula.OP_3 && formula.Z !== undefined) {
               formulaText += ` ${formatOperator(formula.OP_3)} ${formatValue(formula.Z)}%`;
          }

          return formulaText;
     };

     const formattedFormula = formatFormula(ruledata.formula);

     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-percentage me-2 text-success"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         <div className="col-12 d-flex align-items-center">
                              <small className="text-dark fw-semibold">Anwendungsfall:</small>
                              <span className="ms-1 badge bg-light border">{ruledata.MargeFall}</span>
                         </div>
                         <div className="col-12 d-flex align-items-center">
                              <small className="text-dark fw-semibold">Preisanpassung durch:</small>
                              <span className="ms-1 badge bg-light border">{ruledata.margeType}</span>
                         </div>
                         {formattedFormula && (
                              <div className="col-12">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Formel:</small>
                                        <span className="badge bg-light border" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                             {formattedFormula}
                                        </span>
                                   </div>
                              </div>
                         )}
                         {ruledata.minimumPriceFormulaAddTax && (
                              <div className="col-12">
                                   <span className="badge bg-info text-white" style={{ fontSize: '0.7rem' }}>
                                        MwSt. nach Marge aufschlagen
                                   </span>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}

function CalculationChannelAndRulePriceTile({ ruledata, index, onEditRule, onDeleteRule }) {
     const [channelName, setChannelName] = useState(null);
     const [ruleName, setRuleName] = useState(null);

     useEffect(() => {
          const fetchChannels = async () => {
               try {
                    const auth = PPUtils.getLogin();
                    const token = auth.token;

                    const channelsResponse = await RuleEngineBackend.getAllChannels(token);

                    if (channelsResponse && Array.isArray(channelsResponse)) {
                         const matchingChannel = channelsResponse.find(ch => ch.id === ruledata.channel_id);
                         if (matchingChannel) {
                              setChannelName(matchingChannel.channelName);
                         }
                    }
               } catch (error) {
                    console.error('Error fetching channels:', error);
               }
          };

          const fetchRules = async () => {
               if (ruledata.rule_id && ruledata.rule_id !== 0) {
                    try {
                         const request = PPUtils.getCustomerRequestContext();
                         if (request == null) {
                              return;
                         }
                         const { token, customerId } = request;
                         const rulesResponse = await RuleEngineBackend.getAllRules(customerId, token);

                         if (rulesResponse && Array.isArray(rulesResponse)) {
                              const matchingRule = rulesResponse.find(rule => rule.id === ruledata.rule_id);
                              if (matchingRule) {
                                   setRuleName(matchingRule.name);
                              }
                         }
                    } catch (error) {
                         console.error('Error fetching rules:', error);
                    }
               }
          };

          fetchChannels();
          fetchRules();
     }, [ruledata.channel_id, ruledata.rule_id]);

     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-calculator me-2 text-primary"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         {channelName && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Kanal:</small>
                                   <span className="ms-1 badge bg-light border">{channelName}</span>
                              </div>
                         )}
                         {ruleName && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Regel:</small>
                                   <span className="ms-1 badge bg-light border">{ruleName}</span>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}

function RoundPriceTile({ ruledata, index, onEditRule, onDeleteRule }) {
     const getRoundingTypeDisplay = (type) => {
          switch (type) {
               case "IMMER_AUFRUNDEN": return "Immer aufrunden";
               case "IMMER_ABRUNDEN": return "Immer abrunden";
               case "AB_HAELFTE_AUF": return "Ab Hälfte aufrunden";
               case "UPORDOWN": return "Ab Hälfte aufrunden";
               default: return type;
          }
     };

     const getNachkommastelleDisplay = (type) => {
          switch (type) {
               case "ERSTE": return "1. NKS";
               case "ZWEITE": return "2. NKS";
               default: return type;
          }
     };

     const getRoundingRules = () => {
          if (typeof ruledata.roundingValues === 'string') {
               try {
                    const parsed = JSON.parse(ruledata.roundingValues);
                    return Object.entries(parsed).map(([key, value]) => ({ bis: key, wert: value }));
               } catch (e) {
                    return Object.entries(ruledata.roundingValues).map(([key, value]) => ({ bis: key, wert: value }));
               }
          } else if (ruledata.roundingValues && typeof ruledata.roundingValues === 'object') {
               if (!ruledata.roundingValues.formulas) {
                    return Object.entries(ruledata.roundingValues).map(([key, value]) => ({ bis: key, wert: value }));
               }

               return ruledata.roundingValues.formulas || [];
          }

          return [];
     };

     const roundingRules = getRoundingRules();

     const formatRoundingRule = (rule) => {
          if (rule.bis && rule.wert) {
               if (typeof rule.wert === 'string' && rule.wert.includes('NKS')) {
                    return `Bis ${rule.bis}€ -> ${rule.wert}`;
               }
               return `Bis ${rule.bis}€ -> ${rule.wert} runden`;
          } else if (rule.bis && rule.nachkommastelle && rule.rundungAuf !== undefined) {
               return `Bis ${rule.bis}€ -> ${getNachkommastelleDisplay(rule.nachkommastelle)} auf ${rule.rundungAuf} runden`;
          }
          return "Ungültige Rundungsregel";
     };

     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-coins me-2 text-warning"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         <div className="col-12 d-flex align-items-center">
                              <small className="text-dark fw-semibold">Rundungsart:</small>
                              <span className="ms-1 badge bg-light border">{getRoundingTypeDisplay(ruledata.roundingType)}</span>
                         </div>

                         {ruledata.activateContraryBackupRuleRoundMode && (
                              <div className="col-12">
                                   <span className="badge bg-info text-white" style={{ fontSize: '0.7rem' }}>
                                        Gegenoperation bei Backup Regel
                                   </span>
                              </div>
                         )}

                         {roundingRules && roundingRules.length > 0 && (
                              <div className="col-12 mt-2">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Rundungsregeln:</small>
                                        <div className="d-flex flex-wrap">
                                             {roundingRules.map((rule, idx) => (
                                                  <div key={idx} className="badge bg-light border me-1 mb-1">
                                                       {formatRoundingRule(rule)}
                                                  </div>
                                             ))}
                                        </div>
                                   </div>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}

function ChangeIntervalTile({ ruledata, index, onEditRule, onDeleteRule }) {
     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-clock me-2 text-info"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         {ruledata.dates && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Zeitraum:</small>
                                   <span className="ms-1 badge bg-light border" style={{ fontSize: '0.7rem' }}>
                                        {ruledata.dates.replace(',', ' bis ')}
                                   </span>
                              </div>
                         )}
                         <div className="col-12 d-flex align-items-center">
                              <small className="text-dark fw-semibold">Turnus:</small>
                              <span className="ms-1 badge bg-light border">{ruledata.timePeriodTurnus}</span>
                         </div>
                         {ruledata.weekdays?.length > 0 && (
                              <div className="col-12">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Wochentage:</small>
                                        <div className="d-flex flex-wrap">
                                             {ruledata.weekdays.map((day, i) => (
                                                  <span key={i} className="badge bg-primary me-1 mb-1" style={{ fontSize: '0.7rem' }}>{day}</span>
                                             ))}
                                        </div>
                                   </div>
                              </div>
                         )}
                         {ruledata.monthdays?.length > 0 && (
                              <div className="col-12">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Monatstage:</small>
                                        <div className="d-flex flex-wrap">
                                             {ruledata.monthdays.map((day, i) => (
                                                  <span key={i} className="badge bg-secondary me-1 mb-1" style={{ fontSize: '0.7rem' }}>{day}</span>
                                             ))}
                                        </div>
                                   </div>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}

function PriceChangeTile({ ruledata, index, onEditRule, onDeleteRule }) {
     const relations = [];
     if (ruledata.foreignShopStockRuleReference) {
          relations.push({
               targetId: ruledata.foreignShopStockRuleReference,
               targetAnchor: 'right',
               sourceAnchor: 'left',
               style: { strokeColor: "#2650aaff", strokeWidth: 2 }
          });
     }

     const formatComparisonOption = (option) => {
          if (!option) return "";

          const value = typeof option === "object" ? option.value : option;
          const label = typeof option === "object" ? option.label : null;

          switch (value) {
               case "CHEAPEST_FS_PREIS": return "günstigster Preis";
               case "SECOND_CHEAPEST_PRICE": return "zweitgünstigster Preis";
               case "THIRD_CHEAPEST_PRICE": return "drittgünstigster Preis";
               case "FOURTH_CHEAPEST_PRICE": return "viertgünstigster Preis";
               case "FIFTH_CHEAPEST_PRICE": return "fünftgünstigster Preis";
               case "MOST_EXPENSIVE": return "teuerster Preis";
               case "AVERAGE_PRICE":
               case "AVERAGE_PRICE_OF_THREE_CHEAPEST":
               case "AVERAGE_PRICE_OF_ALL": return "Durchschnittspreis";
               case "MEDIAN_PRICE": return "Median";
               case "NEXT_PRICE_ABOVE_MIN_PRICE":
               case "TOP_PRICE": return "Nächster Preis oberhalb des Mindestpreis (Exkl. PA)";
               case "NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE":
               case "TOP_PRICE_INCLUDED_PA": return "Nächster Preis oberhalb des Mindestpreis (Inkl. PA)";
               default: return label || value || "";
          }
     };

     const formatUnit = (isEuro) => {
          return isEuro ? "€" : "%";
     };

     const resolveActionUnit = (action) => {
          const fromType = action?.undercutType;
          if (fromType === "€" || fromType === "%") {
               return fromType;
          }
          return formatUnit(ruledata.typeOfPriceChangeInEuro);
     };

     const formatActionRules = () => {
          if (!ruledata.action || !Array.isArray(ruledata.action) || ruledata.action.length === 0) {
               return [];
          }

          return ruledata.action.map(action => {
               const unit = resolveActionUnit(action);

               if (action.priceUntil !== undefined && action.underBidAmount !== undefined) {
                    const priceUntil = action.priceUntil !== null ? `Bis ${action.priceUntil}€` : "Immer";
                    const underBidAmount = action.underBidAmount !== null ? `${action.underBidAmount}${unit} unterbieten` : "nicht unterbieten";
                    return `${priceUntil} → ${underBidAmount}`;
               }

               if (action.priceType !== undefined && action.underBidAmount !== undefined) {
                    const priceType = action.priceType === "PRICE_WITHOUT_SHIPPING" ? "Preis (ohne Versand)" :
                         action.priceType === "TOTAL_PRICE_WITH_SHIPPING" ? "Gesamtpreis (mit Versand)" : action.priceType;
                    const underBidAmount = action.underBidAmount !== null ? `${action.underBidAmount}${unit} unterbieten` : "nicht unterbieten";
                    return `${priceType || "Preis"} → ${underBidAmount}`;
               }

               return "Unbekannte Regelformat";
          });
     };

     const actionRules = formatActionRules();
     const comparisonOption = formatComparisonOption(ruledata.ComparisonOption);
     const displaysEuro = ruledata.typeOfPriceChangeInEuro ||
          (ruledata.action || []).some((action) => action?.undercutType === "€");

     return (
          <ArcherElement id={ruledata.id} relations={relations}>
               <div className="card mb-2 shadow-sm position-relative" key={index}>
                    <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                         <div className="d-flex align-items-center justify-content-center">
                              <i className="fas fa-exchange-alt me-2 text-success"></i>
                              <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                         </div>
                    </button>
                    <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                         <div className="row g-1">
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Art:</small>
                                   <span className={`ms-1 badge ${displaysEuro ? 'bg-success' : 'bg-primary'} text-white`}>
                                        {displaysEuro ? 'Euro (€)' : 'Prozent (%)'}
                                   </span>
                              </div>
                              {ruledata.priceChangeAsCoupon && (
                                   <div className="col-12">
                                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                                             Als Gutschein auswerten
                                        </span>
                                   </div>
                              )}
                              {comparisonOption && (
                                   <div className="col-12">
                                        <div className="d-flex align-items-start flex-wrap">
                                             <small className="text-dark fw-semibold me-2">Vergleichspreis:</small>
                                             <span className="badge bg-light border" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                                  {comparisonOption}
                                             </span>
                                        </div>
                                   </div>
                              )}
                              {actionRules.length > 0 && (
                                   <div className="col-12 mt-2">
                                        <div className="d-flex align-items-start flex-wrap">
                                             <small className="text-dark fw-semibold me-2">Preisregeln:</small>
                                             <div className="d-flex flex-wrap">
                                                  {actionRules.map((rule, idx) => (
                                                       <div key={idx} className="badge bg-light border me-1 mb-1">
                                                            {rule}
                                                       </div>
                                                  ))}
                                             </div>
                                        </div>
                                   </div>
                              )}
                         </div>
                    </button>
                    {onDeleteRule && (
                         <button
                              type="button"
                              className="pp-rule-delete-btn"
                              onClick={(e) => {
                                   e.stopPropagation();
                                   onDeleteRule(ruledata);
                              }}
                              title="Regel löschen"
                         >
                              <i className="fas fa-trash"></i>
                         </button>
                    )}
               </div>
          </ArcherElement>
     );
}

function MaxPriceChangeTile({ ruledata, index, onEditRule, onDeleteRule }) {
     const formatNumberWithUnit = (value, unit) => {
          const number = finiteNumber(value);
          if (number === null) {
               return "0" + unit;
          }
          return number + unit;
     };

     const formatFormula = (formula, base) => {
          const baseText = displayValue(base);
          if (!formula || !baseText || !hasConfiguredMargeOperand(formula)) return "";

          let result = baseText;

          const formatOperand = (value, isPercentage = false) => {
               if (value === undefined || value === null || isNaN(value) || value === "NaN") {
                    return "0" + (isPercentage ? "%" : "");
               }
               return value + (isPercentage ? "%" : "");
          };

          if (formula.OP_1 && formula.X !== undefined && formula.X !== null) {
               result += ` ${formula.OP_1} ${formatOperand(formula.X)}`;
          }

          if (formula.OP_2 && formula.Y !== undefined && formula.Y !== null) {
               result += ` ${formula.OP_2} ${formatOperand(formula.Y, true)}`;
          }

          if (formula.OP_3 && formula.Z !== undefined && formula.Z !== null && !isNaN(formula.Z)) {
               result += ` ${formula.OP_3} ${formatOperand(formula.Z)}`;
          }

          return result;
     };

     const minFormula = formatFormula(ruledata.minimumPrice, ruledata.minimumPriceFormulaBase);
     const maxFormula = formatFormula(ruledata.maximumPrice, ruledata.maximumPriceFormulaBase);

     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-chart-bar me-2 text-danger"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         {finiteNumber(ruledata.maxPriceDecreaseValue) !== null && finiteNumber(ruledata.maxPriceDecreaseValue) > -1 && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Max. Preisminderung:</small>
                                   <span className="ms-1 badge bg-danger text-white">{formatNumberWithUnit(ruledata.maxPriceDecreaseValue, "%")}</span>
                              </div>
                         )}
                         {finiteNumber(ruledata.maxPriceIncreaseValue) !== null && finiteNumber(ruledata.maxPriceIncreaseValue) > -1 && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Max. Preissteigerung:</small>
                                   <span className="ms-1 badge bg-success text-white">{formatNumberWithUnit(ruledata.maxPriceIncreaseValue, "%")}</span>
                              </div>
                         )}

                         {minFormula && (
                              <div className="col-12">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Mindestpreis:</small>
                                        <div className="d-flex flex-wrap align-items-center">
                                             <span className="badge bg-light border"
                                                  style={{
                                                       fontSize: '0.7rem',
                                                       whiteSpace: 'normal',
                                                       wordBreak: 'break-word',
                                                       overflowWrap: 'break-word',
                                                       hyphens: 'auto'
                                                  }}>
                                                  {minFormula}
                                             </span>
                                             {ruledata.minimumPriceFormulaAddTax && (
                                                  <span className="ms-1 badge bg-info text-white" style={{ fontSize: '0.7rem' }}>
                                                       + MwSt.
                                                  </span>
                                             )}
                                        </div>
                                   </div>
                              </div>
                         )}

                         {maxFormula && (
                              <div className="col-12 mt-2">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Maximalpreis:</small>
                                        <div className="d-flex flex-wrap align-items-center">
                                             <span className="badge bg-light border"
                                                  style={{
                                                       fontSize: '0.7rem',
                                                       whiteSpace: 'normal',
                                                       wordBreak: 'break-word',
                                                       overflowWrap: 'break-word',
                                                       hyphens: 'auto'
                                                  }}>
                                                  {maxFormula}
                                             </span>
                                             {ruledata.maximumPriceFormulaAddTax && (
                                                  <span className="ms-1 badge bg-info text-white" style={{ fontSize: '0.7rem' }}>
                                                       + MwSt.
                                                  </span>
                                             )}
                                        </div>
                                   </div>
                              </div>
                         )}

                         {ruledata.minimumBoundaryEnabled && (
                              <div className="col-12 mt-2 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Min. über EK:</small>
                                   <span className="ms-1 badge bg-light border">{formatNumberWithUnit(ruledata.minimumEuroOverEK ?? 0, "€")}</span>
                              </div>
                         )}

                         {ruledata.minimumBoundaryEnabled && (
                              <div className="col-12 d-flex align-items-center">
                                   <small className="text-dark fw-semibold">Min. Prozent:</small>
                                   <span className="ms-1 badge bg-light border">{formatNumberWithUnit(ruledata.minimumPercent ?? 0, "%")}</span>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}

function AmazonBuyBoxTile({ ruledata, index, onEditRule, onDeleteRule }) {
     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fab fa-amazon me-2 text-warning"></i>
                         <h6 className="card-title mb-0 fw-bold">{ruledata.name}</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <small className="text-muted">
                         Empfohlenen BuyBox-Preis nutzen, wenn keine BuyBox vorhanden ist. Sonst Preisangleichung oder Marge.
                    </small>
               </button>
               {onDeleteRule && (
                    <button
                         className="btn btn-sm btn-outline-danger position-absolute"
                         style={{
                              bottom: "8px",
                              right: "8px",
                              width: "24px",
                              height: "24px",
                              padding: "0",
                              fontSize: "10px"
                         }}
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}